// Business timezone: SAST (UTC+2, no DST) — use for all user-visible timestamps.
const BUSINESS_TIME_ZONE = "Africa/Johannesburg";

const NAIVE_DB_DATETIME_RE = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/;

const isValidNaiveDbDateTimeParts = (month, day, hours, minutes, seconds) =>
  Number(month) <= 12 &&
  Number(day) <= 31 &&
  Number(hours) <= 23 &&
  Number(minutes) <= 59 &&
  Number(seconds) <= 59;

/**
 * ``YYYY-MM-DD HH:mm:ss`` from Flask/db APIs is already GMT+2 wall time — do not shift.
 */
const preserveNaiveDbDateTime = (raw) => {
  const match = String(raw || "").trim().match(NAIVE_DB_DATETIME_RE);
  if (!match) return null;
  const [, year, month, day, hours, minutes, seconds] = match;
  if (!isValidNaiveDbDateTimeParts(month, day, hours, minutes, seconds)) {
    return null;
  }
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

/**
 * Format a valid Date as YYYY-MM-DD HH:mm:ss in the business timezone.
 */
const formatDateToBusinessDateTime = (date) => {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: BUSINESS_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const parts = dtf.formatToParts(date);
  const pick = (type) => parts.find((p) => p.type === type)?.value ?? "";
  const y = pick("year");
  const mo = pick("month");
  const d = pick("day");
  const h = pick("hour");
  const mi = pick("minute");
  const s = pick("second");
  return `${y}-${mo}-${d} ${h}:${mi}:${s}`;
};

export const nowBusinessDateTime = () => formatDateToBusinessDateTime(new Date());

// Utility function for consistent date formatting across the application
export const formatDateTime = (timestamp) => {
  if (!timestamp) return "Never";

  try {
    const raw = String(timestamp).trim();

    // Naive DB/API datetime: backend serializes to GMT+2 wall time already.
    const preserved = preserveNaiveDbDateTime(raw);
    if (preserved) {
      return preserved;
    }

    // whatsapp_API / OpenAPI inbox example: "2026-05-15T10:00:00" — no TZ suffix, stored as UTC.
    const naiveIsoUtc =
      /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(\.\d{1,6})?$/.test(raw) &&
      !/[zZ]$/.test(raw) &&
      !/[+-]\d{2}:?\d{2}$/.test(raw);
    if (naiveIsoUtc) {
      const ms = Date.parse(`${raw}Z`);
      if (!Number.isNaN(ms)) {
        return formatDateToBusinessDateTime(new Date(ms));
      }
    }

    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      throw new Error("Invalid date");
    }

    return formatDateToBusinessDateTime(date);
  } catch (error) {
    console.error("Error formatting date:", error);
    return "Invalid Date";
  }
};

/**
 * Format datetime for systems that store naive values already in business local time.
 * If input is `YYYY-MM-DD HH:mm:ss`, preserve it as-is (no timezone conversion).
 * RFC-1123 / GMT strings are converted to GMT+2 for display.
 */
export const formatDateTimePreserveNaive = (timestamp) => {
  if (!timestamp) return "Never";
  const raw = String(timestamp).trim();
  const preserved = preserveNaiveDbDateTime(raw);
  if (preserved) {
    return preserved;
  }
  if (/\bGMT\b/i.test(raw) || /[zZ]$/.test(raw) || /[+-]\d{2}:?\d{2}$/.test(raw)) {
    return formatDateTime(timestamp);
  }
  return formatDateTime(timestamp);
};

/** DB filter/get responses: naive GMT+2 wall time from backend serialization. */
export const formatDbDateTime = formatDateTimePreserveNaive;

/** Payment register dates: all types stored as SAST naive after migration 018 + Yoco fix. */
export const formatCustomerRegisterPaymentDateTime = (value) => formatDbDateTime(value);

/**
 * UISP device speed test `detail.timestamp`: OpenAPI describes ms since epoch, but some
 * responses use Unix seconds. Values below 1e12 are treated as seconds.
 */
export const formatUispSpeedTestTimestamp = (raw) => {
  if (raw == null || raw === "") {
    return null;
  }
  const n = Number(raw);
  if (!Number.isFinite(n)) {
    return "Invalid Date";
  }
  const ms = n < 1e12 ? n * 1000 : n;
  return formatDateTime(ms);
};

// Utility function for consistent date-only formatting (YYYY-MM-DD)
export const formatDate = (timestamp) => {
  if (!timestamp) return "N/A"; // Or "Never" or an empty string, depending on preference

  try {
    // Attempt to handle cases where timestamp might already be just a date string
    if (typeof timestamp === "string" && /^\d{4}-\d{2}-\d{2}$/.test(timestamp)) {
      const simpleDate = new Date(timestamp + "T00:00:00Z"); // Treat as UTC to avoid timezone shifts
      if (!isNaN(simpleDate.getTime())) {
        if (timestamp === simpleDate.toISOString().slice(0, 10)) {
          const dtf = new Intl.DateTimeFormat("en-US", {
            timeZone: BUSINESS_TIME_ZONE,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          });
          const parts = dtf.formatToParts(simpleDate);
          const pick = (type) => parts.find((p) => p.type === type)?.value ?? "";
          return `${pick("year")}-${pick("month")}-${pick("day")}`;
        }
      }
    }

    // Standard parsing for datetime strings or Date objects
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      // If it's a string that might be a date without time, try appending T00:00:00
      if (typeof timestamp === "string") {
        const dateOnlyAttempt = new Date(timestamp + "T00:00:00Z"); // Treat as UTC
        if (!isNaN(dateOnlyAttempt.getTime())) {
          const dtf = new Intl.DateTimeFormat("en-US", {
            timeZone: BUSINESS_TIME_ZONE,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          });
          const parts = dtf.formatToParts(dateOnlyAttempt);
          const pick = (type) => parts.find((p) => p.type === type)?.value ?? "";
          return `${pick("year")}-${pick("month")}-${pick("day")}`;
        }
      }
      throw new Error("Invalid date");
    }

    const dtf = new Intl.DateTimeFormat("en-US", {
      timeZone: BUSINESS_TIME_ZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    const parts = dtf.formatToParts(date);
    const pick = (type) => parts.find((p) => p.type === type)?.value ?? "";
    return `${pick("year")}-${pick("month")}-${pick("day")}`;
  } catch (error) {
    console.error("Error formatting date (YYYY-MM-DD):", error, "Input:", timestamp);
    return "Invalid Date";
  }
};

/**
 * Values for HTML `input type="date"` must be `YYYY-MM-DD`. Flask's default JSON
 * encodes Python `date` as RFC 2822 (e.g. "Wed, 18 Apr 2026 00:00:00 GMT"), which
 * breaks naive `slice(0, 10)` parsing.
 */
export const toHtmlDateInputValue = (raw) => {
  if (raw == null || raw === "") return "";
  const s = String(raw).trim();
  const isoPrefix = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoPrefix) return isoPrefix[1];
  const parsed = Date.parse(s);
  if (Number.isNaN(parsed)) return "";
  const d = new Date(parsed);
  const y = d.getUTCFullYear();
  const mo = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${mo}-${day}`;
};
